import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Flag from "react-world-flags";

const languages = [
  { code: "en", name: "English", countryCode: "GB" },
  { code: "it", name: "Italiano", countryCode: "IT" },
  { code: "de", name: "Deutsch", countryCode: "DE" },
  { code: "fr", name: "Français", countryCode: "FR" },
  { code: "es", name: "Español", countryCode: "ES" },
];

// Chevron Down Icon
const ChevronDown = ({ className }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
    />
  </svg>
);

export default function LanguageSwitcher({ className = "", dropUp = false, compact = false }) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const attuale = i18n.resolvedLanguage || i18n.language;
  const currentLanguage =
    languages.find((lang) => lang.code === attuale) || languages[0];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center text-sm text-current transition-opacity hover:opacity-75 ${
          compact ? "gap-1 px-1 py-2" : "gap-2 px-3 py-2"
        }`}
      >
        <div className="w-5 h-5 rounded-full overflow-hidden ring-2 ring-white/20 flex-shrink-0">
          <Flag
            code={currentLanguage.countryCode}
            alt={currentLanguage.name}
            className="w-full h-full object-cover scale-110"
          />
        </div>
        {!compact && (
          <span className="font-medium">{currentLanguage.name}</span>
        )}
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      <div
        className={`absolute right-0 w-40 rounded-lg bg-[#6b6559] shadow-xl ring-1 ring-black/10 transition-all duration-150 z-10 ${
          dropUp ? "bottom-full mb-2 origin-bottom-right" : "top-full mt-2 origin-top-right"
        } ${
          isOpen ? "opacity-100 scale-100 visible" : "opacity-0 scale-95 invisible"
        }`}
      >
        <div className="py-1">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                attuale === lang.code
                  ? "bg-white/20 text-white font-medium"
                  : "text-white/90 hover:bg-white/20 hover:text-white"
              }`}
            >
              <div className="w-5 h-5 rounded-full overflow-hidden ring-1 ring-white/20 flex-shrink-0">
                <Flag
                  code={lang.countryCode}
                  alt={lang.name}
                  className="w-full h-full object-cover scale-100"
                />
              </div>
              <span>{lang.name}</span>
              {attuale === lang.code && (
                <svg
                  className="w-4 h-4 ml-auto"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
